import React, { ReactElement, useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'

import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'
import { LoadingSpinner } from '@dfds-pax/shared-components/client'
import { isJerseyProduct } from '@dfds-pax/utils'

import YesIcon from '@/ui-cvi/components/dxp-icons/system/YesIcon'

import { useTranslation } from '@/hooks/useDebugTranslations'

import TicketInfo from './TicketInfo'
import TierSelector from './TierSelector'

interface DepartureTierProps {
  components?: BffInterfacesDeparture.DeparturePricesComponent[]
  currency: string
  departureId: string
  hidePriceAdjustment: boolean
  loading: boolean
  offerApplied: boolean
  offerOnExtras: boolean
  offerPrice?: number
  onTierSelection: () => void
  price: number
  productCode: string
  radioName: string
  selectionLoading: boolean
  selectedType: boolean
  soldOut: boolean
  ticketRef?: React.RefObject<HTMLDivElement>
}

const DepartureTierSingle = ({
  components,
  currency,
  departureId,
  hidePriceAdjustment,
  loading,
  offerPrice,
  onTierSelection,
  price,
  productCode,
  selectedType,
  selectionLoading,
  soldOut,
}: DepartureTierProps): ReactElement => {
  const ticketRef = useRef<HTMLDivElement | null>(null)
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true)
  const { t } = useTranslation()
  const onSelection = () => {
    onTierSelection()
    if (ticketRef?.current) {
      ticketRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' })
    }
  }

  useEffect(() => {
    if (!loading) setShowSkeleton(false)
  }, [loading])

  const showLoader = showSkeleton || selectionLoading

  return (
    <div className="gap-primitive-16 flex w-full">
      <div className="hidden flex-1 lg:block">
        <TicketInfo productCode={productCode} soldOut={soldOut} />
      </div>
      <div
        className={clsx(
          'focus-visible:outline-semantic-global-primary-default bg-semantic-global-background-white flex w-full flex-col border-solid transition-all duration-200 ease-in-out focus-visible:outline-2 focus-visible:outline-offset-2 md:min-w-[200px] lg:ml-auto lg:w-[50%]',
          selectedType ? 'border-semantic-global-primary-default border-2' : 'border-semantic-global-border-light border-2',
          (selectedType || soldOut || loading) && 'cursor-default',
          !selectedType && !soldOut && !loading && 'cursor-pointer',
          soldOut && 'h-full',
          !soldOut && !selectedType && !loading && 'hover:border-semantic-global-primary-default'
        )}
        data-e2e="departure-tier-card"
        data-e2e-selected={selectedType}
        ref={ticketRef}
      >
        <div className={clsx('relative flex flex-1 flex-col')}>
          {showLoader ? (
            <div
              className={clsx(
                'bg-semantic-global-background-white absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center'
              )}
            >
              <div className="z-10">
                <LoadingSpinner darkModeVariant="default" size="xl" />
              </div>
            </div>
          ) : null}
          <div className={clsx(showLoader && 'opacity-50')}>
            <TierSelector
              components={components}
              currency={currency}
              departureId={departureId}
              hidePriceAdjustment={hidePriceAdjustment}
              offerPrice={offerPrice}
              onTierSelection={() => !selectedType && !soldOut && !loading && onSelection()}
              price={price}
              productCode={productCode}
              selectedType={selectedType}
              soldOut={soldOut}
            />
            <div
              className={clsx(isJerseyProduct(productCode) && 'lg:hidden')}
              onClick={() => !selectedType && !soldOut && !loading && onSelection()}
            >
              <TicketInfo productCode={productCode} soldOut={soldOut} />
              <div
                className={clsx(
                  'px-semantic-global-md pb-semantic-global-md text-semantic-global-text-default flex flex-row items-center gap-2',
                  !selectedType && 'hidden'
                )}
              >
                <p className={clsx('typography-body-md-medium')}>{t('TICKET-SELECTED')}</p>
                <span className="bg-semantic-global-success-default text-semantic-global-background-white flex size-6 shrink-0 items-center justify-center rounded-full">
                  <YesIcon size="md" />
                </span>
              </div>
            </div>
          </div>
          <div
            className={clsx(
              'p-semantic-global-md text-semantic-global-text-default hidden flex-row items-center gap-2 lg:flex',
              !selectedType && 'hidden'
            )}
          >
            <p className={clsx('typography-body-md-medium')}>{t('TICKET-SELECTED')}</p>
            <span className="bg-semantic-global-success-default text-semantic-global-background-white flex size-6 shrink-0 items-center justify-center rounded-full">
              <YesIcon size="md" />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface Props {
  departureId: string
  loading: boolean
  offerApplied: boolean
  onDepartureTierSelection: (ticket: BffInterfacesDeparture.Ticket) => void
  returnLeg: boolean
  selectionLoading: boolean
  selectedType: string | false
  ticket: BffInterfacesDeparture.Ticket
}

const DepartureTierGroupSingle: React.FC<Props> = ({
  departureId,
  loading,
  offerApplied,
  onDepartureTierSelection,
  returnLeg,
  selectedType,
  selectionLoading,
  ticket,
}): ReactElement => {
  const [selectedProduct, setSelectedProduct] = useState<string>()

  useEffect(() => {
    if (selectedType) {
      setSelectedProduct(selectedType && ticket.productCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType])
  return (
    <div className="gap-primitive-16">
      <DepartureTierSingle
        components={ticket.components}
        currency={ticket.currency}
        departureId={departureId}
        hidePriceAdjustment={!offerApplied || ticket.ticketOffer?.hidePriceAdjustment || false}
        loading={loading}
        offerApplied={offerApplied}
        offerOnExtras={ticket.offerOnExtras}
        offerPrice={offerApplied ? ticket.ticketOffer?.price : undefined}
        onTierSelection={() => onDepartureTierSelection(ticket)}
        price={ticket.price}
        productCode={ticket.productCode}
        radioName={returnLeg ? `departures-return-economy-radio` : `departures-outbound-radio`}
        selectionLoading={selectionLoading}
        selectedType={selectedProduct === ticket.productCode}
        soldOut={ticket?.soldOut}
      />
    </div>
  )
}

export default DepartureTierGroupSingle

export interface Tier {
  currency: string
  price: number
  productCode: string
  soldOut: boolean
}
